import { ChangeEvent, FC, useEffect, useState } from 'react';

import RichText from '@eneco/components/RichText/RichText';
import { TrackedDialog } from '@eneco/components/TrackedDialog/TrackedDialog';
import { TrackedNotificationBox } from '@eneco/components/TrackedNotificationBox/TrackedNotificationBox';
import useDC from '@eneco/dc/useDC';
import { Layout, NextButton, Overview, StepHeader } from '@eneco/flows/components';
import { useFlowHooks } from '@eneco/flows/utils/FlowProvider';
import { typedYupResolver } from '@eneco/hooks/form';
import { useFormatter } from '@eneco/i18n';
import { Bleed, Box, Heading, InputText, Stack, Text, TextLink } from '@eneco/sparky';
import { usePlaceholderContent } from '@sitecore/common';
import { SMESummaryStepRendering } from '@sitecore/types/SMESummaryStep';
import { useMachine } from '@xstate/react';
import { friendlyFormatIBAN, isValidIBAN } from 'ibantools';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';

import { ProductsPricingBreakdown, MergedFields } from './ProductPricingBreakdown';
import { SmeFlowError, Ribbon, TermsAndConditions } from '../../components';
import { FlowContextSme } from '../../context';
import {
  checkOfferIncludesDynamicPricingProduct,
  checkOfferIncludesNonDynamicPricingProduct,
  getCashbackTexts,
  getOffer,
  getOfferTermsAndConditions,
} from '../../helpers';
import { useCachedOfferMachine, useFlowTracking } from '../../hooks';
import { orderMachine } from '../../machines';

interface FormFields {
  iban?: string;
}

export const SummaryStep: FC = () => {
  const { useFlowActorRef, useFlowSelector } = useFlowHooks<FlowContextSme>();
  const { send: sendToFlowMachine } = useFlowActorRef();
  const [orderState, sendToOrderMachine] = useMachine(orderMachine);
  const {
    context: { orderTypes: switchTypes },
  } = orderState;

  const flowContext = useFlowSelector(state => state.context);

  const {
    initials,
    firstName,
    surnamePreposition,
    surname,
    postalCode,
    houseNumber,
    houseNumberSuffix,
    street,
    city,
    iban,
    phoneNumber,
    emailAddress,
    isSME,
    isPersonalDetailsEditProhibited,
    companyName,
    chamberOfCommerceNumber,
  } = flowContext;

  const { SMESummaryStep: { fields: textualData } = {} } = usePlaceholderContent<{
    SMESummaryStep: SMESummaryStepRendering;
  }>();

  const { businessUnit, label } = useDC();
  const { offerState, sendToOfferMachine } = useCachedOfferMachine(businessUnit, label);

  const offer = offerState?.context?.offer;

  const formSchema = yup.object({
    ...(!isPersonalDetailsEditProhibited && {
      iban: yup
        .string()
        .required(textualData?.paymentDetails?.ibanFormField?.value?.requiredMessage)
        .test(
          'iban validation',
          textualData?.paymentDetails?.ibanFormField?.value?.validationMessage ?? '',
          iban => !!(iban && isValidIBAN(iban.replace(/\s+/g, '').toUpperCase())),
        ),
    }),
  });

  const { trackPurchase, trackFillUsage } = useFlowTracking();
  const { currency, format, address } = useFormatter();

  const [isLoading, setIsLoading] = useState(true);
  const [isSuccessful, setIsSuccessful] = useState(false);
  const [isFailed, setIsFailed] = useState(false);

  const [isNotFound, setIsNotFound] = useState(false);
  const [isNotAvailable, setIsNotAvailable] = useState(false);
  const [isNotAuthenticated, setIsNotAuthenticated] = useState(false);

  const [isOrderFailed, setIsOrderFailed] = useState(false);

  const selectedOffer = getOffer(flowContext, offer);
  const selectedOfferTermsAndConditions = getOfferTermsAndConditions(flowContext, offer);
  const selectedCashbackTexts = getCashbackTexts(
    selectedOffer?.costDiscounts?.cashback?.type,
    textualData?.promotionTexts,
  );

  const shouldShowDynamicPricingContractNotification =
    checkOfferIncludesDynamicPricingProduct(selectedOffer) &&
    !checkOfferIncludesNonDynamicPricingProduct(selectedOffer) &&
    !!textualData?.notifications?.dynamicPricingContractNotification?.value?.content;

  const shouldShowHybridContractNotification =
    checkOfferIncludesDynamicPricingProduct(selectedOffer) &&
    checkOfferIncludesNonDynamicPricingProduct(selectedOffer) &&
    !!textualData?.notifications?.hybridContractNotification?.value?.content;

  const {
    handleSubmit,
    getValues,
    register,
    formState: { errors, isSubmitSuccessful },
  } = useForm<FormFields>({
    mode: 'onSubmit',
    defaultValues: { iban },
    resolver: typedYupResolver<FormFields>(formSchema),
  });

  useEffect(() => {
    sendToOfferMachine({ type: 'GET_OFFER', flowContext });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- lifecycle componentDidMount
  }, []);

  useEffect(() => {
    if (isSubmitSuccessful) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const payload: any = {
        type: 'SEND_ORDER',
        config: {
          businessUnit,
          label,
        },
        values: { ...flowContext, ...getValues() },
        offer: offer ?? { isMkb: !!isSME },
      };
      sendToOrderMachine(payload);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubmitSuccessful]);

  useEffect(() => {
    setIsOrderFailed(orderState.matches('ERROR'));

    if (orderState.matches('SUCCESS')) {
      selectedOffer && trackPurchase(selectedOffer, offer, switchTypes);
      trackFillUsage({ ...flowContext });

      sendToFlowMachine({
        type: 'NEXT_PAGE',
        values: { ...getValues() },
      });
    }

    if (orderState.matches('ERROR')) {
      setIsSuccessful(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderState]);

  useEffect(() => {
    if (!offerState.matches('IDLE')) {
      setIsLoading(offerState.matches('FETCHING'));
      setIsFailed(!offerState.matches('FETCHING') && !offerState.matches('SUCCESS'));
      setIsSuccessful(offerState.matches('SUCCESS'));

      setIsNotFound(offerState.matches('ERROR_NOT_FOUND'));
      setIsNotAvailable(offerState.matches('ERROR_NOT_AVAILABLE'));
      setIsNotAuthenticated(offerState.matches('ERROR_NOT_AUTHENTICATED'));
    }
  }, [offerState]);

  const getErrorNotification = () => {
    if (isNotAuthenticated) return textualData?.genericError?.errorNotAuthenticatedNotification;
    if (isNotAvailable) return textualData?.genericError?.errorNotAvailableNotification;
    if (isNotFound) return textualData?.genericError?.errorNotFoundNotification;
    if (isOrderFailed) return textualData?.genericError?.sendOrderErrorNotification;

    return textualData?.genericError?.fetchOfferErrorNotification;
  };

  const getErrorButtonLabel = () => {
    if (isNotAuthenticated) return textualData?.genericError?.loginButtonText;
    if (!isNotAvailable && !isNotFound) return textualData?.genericError?.tryAgainButtonText;

    return undefined;
  };

  return (
    <Layout.Content variant="B" isLoading={isLoading} handleSubmit={handleSubmit}>
      {isSuccessful && (
        <>
          <StepHeader textualData={textualData} />
          {shouldShowDynamicPricingContractNotification && (
            <TrackedNotificationBox
              isAlert={false}
              label={textualData?.notifications?.dynamicPricingContractNotification?.value?.title}
              title={textualData?.notifications?.dynamicPricingContractNotification?.value?.title ?? ''}
              text={<RichText html={textualData?.notifications?.dynamicPricingContractNotification?.value?.content} />}
              variant={textualData?.notifications?.dynamicPricingContractNotification?.value?.variant}
            />
          )}

          {shouldShowHybridContractNotification && (
            <TrackedNotificationBox
              isAlert={false}
              label={textualData?.notifications?.hybridContractNotification?.value?.title}
              title={textualData?.notifications?.hybridContractNotification?.value?.title ?? ''}
              text={<RichText html={textualData?.notifications?.hybridContractNotification?.value?.content} />}
              variant={textualData?.notifications?.hybridContractNotification?.value?.variant}
            />
          )}
          <Stack.Item>
            <Overview>
              <Overview.Header>{textualData?.personalDetailsOverview?.title?.value}</Overview.Header>

              <Overview.Details
                type="company"
                title={textualData?.companyDetailsOverview?.title?.value ?? ''}
                editButton={{
                  label: textualData?.companyDetailsOverview?.editDetailsLabel?.value ?? '',
                  onClick: () => sendToFlowMachine({ type: 'GOTO', stepName: 'STEP_COMPANY_DETAILS' }),
                }}>
                <Text as="p">
                  {textualData?.companyDetailsOverview?.companyNameLabel?.value}: {companyName}
                </Text>
                <Text as="p">
                  {textualData?.companyDetailsOverview?.chamberOfCommerceNumberLabel?.value}: {chamberOfCommerceNumber}
                </Text>
              </Overview.Details>

              <Overview.Details
                type="profile"
                title={textualData?.personalDetailsOverview?.personalDetailsLabel?.value ?? ''}
                editButton={
                  isPersonalDetailsEditProhibited
                    ? undefined
                    : {
                        label: textualData?.personalDetailsOverview?.editDetailsLabel?.value ?? '',
                        onClick: () => sendToFlowMachine({ type: 'GOTO', stepName: 'STEP_COMPANY_DETAILS' }),
                      }
                }>
                <Text as="p">
                  {textualData?.personalDetailsOverview?.nameLabel?.value}:{' '}
                  {`${firstName || initials} ${
                    surnamePreposition ? surnamePreposition + ' ' : ''
                  }${surname}`}
                </Text>
                <Text as="p">
                  {textualData?.personalDetailsOverview?.phoneNumberLabel?.value}: {phoneNumber}
                </Text>
                <Text as="p">
                  {textualData?.personalDetailsOverview?.emailAddressLabel?.value}: {emailAddress}
                </Text>
              </Overview.Details>

              <Overview.Details
                type="address"
                title={textualData?.personalDetailsOverview?.addressDetailsLabel?.value ?? ''}
                editButton={
                  isPersonalDetailsEditProhibited
                    ? undefined
                    : {
                        label: textualData?.personalDetailsOverview?.editDetailsLabel?.value ?? '',
                        onClick: () => sendToFlowMachine({ type: 'GOTO', stepName: 'STEP_ADDRESS' }),
                      }
                }>
                <Text as="p">{address.streetAddress({ street, houseNumber, houseNumberSuffix })}</Text>
                <Text as="p">{address.postalCodeCity({ postalCode, city })}</Text>
              </Overview.Details>
            </Overview>
          </Stack.Item>

          <Stack.Item>
            <Overview>
              <Overview.Header>{textualData?.productsOverview?.title?.value}</Overview.Header>

              {selectedCashbackTexts && (
                <Bleed top="6">
                  <Ribbon
                    size="large"
                    emphasis="medium"
                    title={format(selectedCashbackTexts.title, {
                      [`${selectedOffer?.costDiscounts?.cashback?.type}`]: currency.euroNoFractionDigits(
                        selectedOffer?.costDiscounts?.cashback?.value ?? 0,
                      ),
                    })}
                    text={selectedCashbackTexts?.text}
                    trigger={selectedCashbackTexts?.trigger}
                  />
                </Bleed>
              )}

              {selectedOffer && (
                <Box paddingX="6">
                  <ProductsPricingBreakdown
                    textualData={textualData as MergedFields}
                    offer={selectedOffer}
                    contractStartDateLabel={textualData?.productsOverview?.contractStartDateLabel?.value}
                    contractEndDateLabel={textualData?.productsOverview?.contractEndDateLabel?.value}
                  />
                </Box>
              )}
            </Overview>
          </Stack.Item>

          {!isPersonalDetailsEditProhibited && (
            <>
              <Stack.Item>
                <Stack direction="column" gap="4">
                  <Heading as="h3" size="XS">
                    {textualData?.paymentDetails?.title?.value}
                  </Heading>
                  <Text>{textualData?.paymentDetails?.description?.value}</Text>
                </Stack>
              </Stack.Item>

              <Stack.Item>
                <InputText
                  {...register('iban', {
                    onChange: (event: ChangeEvent<HTMLInputElement>) => {
                      const target = event.target;

                      const caretPosition = target.selectionStart;

                      const currentValue = target.value;
                      const newValue = friendlyFormatIBAN(currentValue) ?? '';

                      target.value = newValue;
                      target.selectionEnd =
                        currentValue.length !== newValue.length ? (caretPosition ?? 0) + 1 : caretPosition;
                    },
                  })}
                  label={textualData?.paymentDetails?.ibanFormField?.value?.label ?? ''}
                  hint={textualData?.paymentDetails?.ibanFormField?.value?.hint}
                  placeholder={textualData?.paymentDetails?.ibanFormField?.value?.placeholder}
                  error={errors.iban?.message}
                />

                <Box paddingTop="3">
                  <Text size="BodyS" color="textLowEmphasis">
                    <TrackedDialog
                      title={textualData?.paymentDetails?.ibanDialog?.value?.title}
                      trigger={<TextLink>{textualData?.paymentDetails?.ibanDialog?.value?.triggerText}</TextLink>}>
                      <RichText html={textualData?.paymentDetails?.ibanDialog?.value?.content} />
                    </TrackedDialog>
                  </Text>
                </Box>
              </Stack.Item>
            </>
          )}

          <TermsAndConditions
            flowContext={flowContext}
            textualData={textualData}
            offer={selectedOffer}
            offerTermsAndConditions={selectedOfferTermsAndConditions}
          />

          <NextButton isLoading={isSubmitSuccessful}>{textualData?.content?.nextStepText?.value}</NextButton>

          {textualData?.content?.energyDisputeCommitteeText?.value && (
            <Text size="BodyXS" color="textLowEmphasis">
              {textualData?.content?.energyDisputeCommitteeText?.value}
            </Text>
          )}
        </>
      )}

      {(isFailed || isOrderFailed) && (
        <SmeFlowError
          type={isNotAuthenticated ? 'AUTHENTIFICATION' : 'GENERIC'}
          textualData={{
            notification: getErrorNotification(),
            buttonLabel: getErrorButtonLabel(),
          }}
        />
      )}
    </Layout.Content>
  );
};
